import baseAPI from "@/api/base";
import {
  CREATE_SETUP_CHECKOUT_URL,
  DOWNLOAD_INVOICE_URL,
  EXPORT_USAGE_HISTORY_URL,
  EXPORT_ENRICHMENT_DETAILS_URL,
  GET_BILLING_ESTIMATE_URL,
  GET_ENRICHMENT_DETAILS_URL,
  GET_INVOICES_URL,
  GET_PAYMENT_METHODS_URL,
  GET_SUBSCRIPTION_STATUS_URL,
  GET_TRIAL_STATUS_URL,
  GET_USAGE_HISTORY_URL,
  GET_USAGE_STATS_URL,
  START_FREE_TRIAL_URL,
  PROMO_CODE_VALIDATE_URL,
  PROMO_CODE_APPLY_URL,
  CREDITS_PURCHASE_URL,
  CREDITS_BALANCE_URL,
  CREDITS_HISTORY_URL,
  CREDITS_CHECKOUT_URL,
  PLAN_SWITCH_PREVIEW_URL,
  PLAN_SWITCH_URL,
  PLAN_DOWNGRADE_CANCEL_URL,
} from "@/utils/urls";
import { AxiosResponse } from "axios";

interface UsageStatsParams {
  startDate?: string;
  endDate?: string;
  userId?: string;
}

interface UsageHistoryParams {
  startDate?: string;
  endDate?: string;
  usageType?: string;
  userId?: string;
  page?: number;
  perPage?: number;
}

interface EnrichmentDetailsParams {
  type: "email" | "phone";
  startDate?: string;
  endDate?: string;
  userId?: string;
  page?: number;
  limit?: number;
}

class Billing {
  static getUsageStats = async (params: UsageStatsParams): Promise<AxiosResponse> => {
    const mappedParams: Record<string, unknown> = {};
    if (params.startDate) mappedParams.start_date = params.startDate;
    if (params.endDate) mappedParams.end_date = params.endDate;
    if (params.userId) mappedParams.user_id = params.userId;
    const response: AxiosResponse = await baseAPI.get(GET_USAGE_STATS_URL, {
      params: mappedParams,
    });
    return response;
  };

  static getUsageHistory = async (params: UsageHistoryParams): Promise<AxiosResponse> => {
    const mappedParams: Record<string, unknown> = {};
    if (params.startDate) mappedParams.start_date = params.startDate;
    if (params.endDate) mappedParams.end_date = params.endDate;
    if (params.userId) mappedParams.user_id = params.userId;
    if (params.usageType) mappedParams.service_key = params.usageType;
    if (params.page) mappedParams.page = params.page;
    if (params.perPage) mappedParams.limit = params.perPage;
    const response: AxiosResponse = await baseAPI.get(GET_USAGE_HISTORY_URL, {
      params: mappedParams,
    });
    return response;
  };

  static exportUsageHistory = async (params: UsageHistoryParams): Promise<AxiosResponse> => {
    const mappedParams: Record<string, unknown> = {};
    if (params.startDate) mappedParams.start_date = params.startDate;
    if (params.endDate) mappedParams.end_date = params.endDate;
    if (params.userId) mappedParams.user_id = params.userId;
    if (params.usageType) mappedParams.service_key = params.usageType;
    const response: AxiosResponse = await baseAPI.get(EXPORT_USAGE_HISTORY_URL, {
      params: mappedParams,
      responseType: "blob",
    });
    return response;
  };

  static getInvoices = async (): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_INVOICES_URL);
    return response;
  };

  static getPaymentMethods = async (): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_PAYMENT_METHODS_URL);
    return response;
  };

  static downloadInvoice = async (invoiceId: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(`${DOWNLOAD_INVOICE_URL}/${invoiceId}/pdf`, {
      responseType: "blob",
    });
    return response;
  };

  static getSubscriptionStatus = async (): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_SUBSCRIPTION_STATUS_URL);
    return response;
  };

  static startFreeTrial = async (): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(START_FREE_TRIAL_URL);
    return response;
  };

  static getTrialStatus = async (): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_TRIAL_STATUS_URL);
    return response;
  };

  static createSetupCheckout = async (returnPath?: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(CREATE_SETUP_CHECKOUT_URL, { returnPath });
    return response;
  };

  static getBillingEstimate = async (): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_BILLING_ESTIMATE_URL);
    return response;
  };

  static validatePromoCode = async (code: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(PROMO_CODE_VALIDATE_URL, { code });
    return response;
  };

  static applyPromoCode = async (code: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(PROMO_CODE_APPLY_URL, { code });
    return response;
  };

  static getEnrichmentDetails = async (params: EnrichmentDetailsParams): Promise<AxiosResponse> => {
    const mappedParams: Record<string, unknown> = { type: params.type };
    if (params.startDate) mappedParams.start_date = params.startDate;
    if (params.endDate) mappedParams.end_date = params.endDate;
    if (params.userId) mappedParams.user_id = params.userId;
    if (params.page) mappedParams.page = params.page;
    if (params.limit) mappedParams.limit = params.limit;
    const response: AxiosResponse = await baseAPI.get(GET_ENRICHMENT_DETAILS_URL, {
      params: mappedParams,
    });
    return response;
  };

  static exportEnrichmentDetails = async (
    params: Omit<EnrichmentDetailsParams, "page" | "limit">
  ): Promise<AxiosResponse> => {
    const mappedParams: Record<string, unknown> = { type: params.type };
    if (params.startDate) mappedParams.start_date = params.startDate;
    if (params.endDate) mappedParams.end_date = params.endDate;
    if (params.userId) mappedParams.user_id = params.userId;
    const response: AxiosResponse = await baseAPI.get(EXPORT_ENRICHMENT_DETAILS_URL, {
      params: mappedParams,
      responseType: "blob",
    });
    return response;
  };

  static purchaseCredits = async (amountCents: number): Promise<AxiosResponse> => {
    return await baseAPI.post(CREDITS_PURCHASE_URL, { amountCents });
  };

  static createCreditsCheckout = async (
    amountCents: number,
    returnPath = "/settings/billing"
  ): Promise<AxiosResponse> => {
    return await baseAPI.post(CREDITS_CHECKOUT_URL, { amountCents, returnPath });
  };

  static getCreditBalance = async (): Promise<AxiosResponse> => {
    return await baseAPI.get(CREDITS_BALANCE_URL);
  };

  static getCreditHistory = async (params: {
    page?: number;
    limit?: number;
    type?: string;
  }): Promise<AxiosResponse> => {
    return await baseAPI.get(CREDITS_HISTORY_URL, { params });
  };

  static getActivePowerUps = async (orgId: string): Promise<AxiosResponse> => {
    return baseAPI.get(`/orgs/${orgId}/powerups`);
  };

  static getPlanSwitchPreview = async (
    targetInterval: "annual" | "monthly"
  ): Promise<AxiosResponse> => {
    return baseAPI.get(PLAN_SWITCH_PREVIEW_URL, { params: { targetInterval } });
  };

  static switchPlan = async (targetInterval: "annual" | "monthly"): Promise<AxiosResponse> => {
    return baseAPI.post(PLAN_SWITCH_URL, { targetInterval });
  };

  static cancelPlanDowngrade = async (): Promise<AxiosResponse> => {
    return baseAPI.delete(PLAN_DOWNGRADE_CANCEL_URL);
  };
}

export default Billing;
